import React, { useEffect, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { VIDEO_DURATION, VIDEO_URL } from '../../data/config';
import { PlayIcon } from '../../assets/icons/icons';
import styles from './VideoCard.module.scss';

interface VideoCardProps {
    compact?: boolean;
    onOpen?: () => void;
}

export const VideoCard: FC<VideoCardProps> = ({ compact = false, onOpen }) => {
    const [open, setOpen] = useState(false);

    const openPlayer = (): void => {
        setOpen(true);
        onOpen?.();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        openPlayer();
    };

    useEffect(() => {
        if (!open) return undefined;

        const onEsc = (event: globalThis.KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false);
        };

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onEsc);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    return (
        <>
            <div
                className={`${styles.card} ${compact ? styles.compact : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Открыть видеоинструкцию"
                onClick={openPlayer}
                onKeyDown={handleKeyDown}
            >
                <div className={styles.hero}>
                    <video
                        src={`${VIDEO_URL}#t=0.1`}
                        preload="metadata"
                        muted
                        playsInline
                        tabIndex={-1}
                        aria-hidden="true"
                    />

                    <div className={styles.copy}>
                        <div className={styles.eyebrow}>Видеоинструкция</div>

                        <h3>Посмотрите сервис в работе</h3>

                        <p>
                            Короткий обзор основных сценариев: создание проекта, поиск предложений и работа с
                            результатами.
                        </p>
                    </div>

                    <button type="button" className={styles.cta} tabIndex={-1}>
                        <PlayIcon />
                        Смотреть видео
                    </button>

                    <div className={styles.bigPlay} aria-hidden="true">
                        <PlayIcon />
                    </div>

                    <div className={styles.duration}>{VIDEO_DURATION}</div>
                </div>
            </div>

            {open &&
                createPortal(
                    <div
                        className={styles.overlay}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Видеоинструкция"
                        onClick={() => setOpen(false)}
                    >
                        <div className={styles.player} onClick={(event) => event.stopPropagation()}>
                            <button
                                type="button"
                                className={styles.close}
                                aria-label="Закрыть видео"
                                onClick={() => setOpen(false)}
                            >
                                <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                                    <path
                                        d="M5 5l10 10M15 5L5 15"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>

                            <video src={VIDEO_URL} controls autoPlay playsInline />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
};